import React, { useMemo, useState } from "react";
import "./Form.css";

// ✅ Use env first (good for hosting), fallback to localhost
const API_BASE =
  process.env.REACT_APP_API_BASE?.replace(/\/$/, "") || "http://localhost:5001";
  

function isMobileDevice() {
  const ua = navigator.userAgent || "";
  const isMobileUA =
    /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(ua);
  const hasTouch =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;
  return isMobileUA || hasTouch;
}

/** Step 2 = Branche (Industry) */
const CATEGORIES = [
  "Designer",
  "Händler",
  "Privat",
  "Servicepartner",
  "Werft",
  "Zulieferer",
  "Aftersales & Service",
];

const ZULIEFERER_TYPES = ["Elektro", "Hydraulik", "Mechanik"];

const INTEREST_SERVICEPARTNER = ["Schulung", "Ersatzteilanfrage"];
const INTEREST_AFTERSALES = ["Technische Fragen", "Ersatzteilanfrage"];

const PRODUCT_OPTIONS = [
  "Ocean Access",
  "Lift",
  "Hinge / Hatch",
  "Fold down / balcony",
  "Bathing ladder",
  "Ascender",
  "Winch",
  "Platform locking system",
  "Crane",
  "Launch system",
  "Cradle",
  "Sonstiges",
];

const TRAGKRAFT_OPTIONS = ["350kg", "600kg", "800kg", "1.000kg", "Sonstiges"];

const MAX_FILES = 25;
const MAX_FILE_MB = 25;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

function uniq(arr) {
  return Array.from(new Set(arr.filter(Boolean)));
}

export default function App() {
  const mobile = useMemo(() => isMobileDevice(), []);
  const [stepIndex, setStepIndex] = useState(0);

  const [form, setForm] = useState({
    // Step 1: Contact + venue
    
    venue: "",
    submitter: "",
    firstName: "",
    lastName: "",
    customerNumber: "",
    company: "",
    phone: "",
    email: "",
    website: "",

    // Step 2: Branche
    category: "",

    // Conditional
    zuliefererType: "",
    interest: "",

    // Spare part fields
    boatType: "",
    serialNumber: "",
    sparePartType: "",

    // Products
    products: [],
    productsOther: "",

    // Product details
    tragkraft: "",

    // Notes
    notes: "",
  });

  const [cardFile, setCardFile] = useState(null);
  const [cardPreviewUrl, setCardPreviewUrl] = useState("");
  const [loadingCard, setLoadingCard] = useState(false);

  const [attachments, setAttachments] = useState([]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleInArray(key, value) {
    setForm((prev) => {
      const arr = Array.isArray(prev[key]) ? prev[key] : [];
      const exists = arr.includes(value);
      const next = exists ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
  }

  function onCardFileChange(file) {
    setCardFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setCardPreviewUrl(url);
    } else {
      setCardPreviewUrl("");
    }
  }

  // ✅ FIX: Support BOTH response formats:
  // 1) { firstName, lastName, ... }
  // 2) { success:true, parsed:{ firstName, ... }, text:"..." }
  async function autoFillFromCard() {
    if (!cardFile) {
      alert("Please select a business card photo first.");
      return;
    }

    try {
      setLoadingCard(true);
      const fd = new FormData();
      fd.append("cardImage", cardFile);

      const res = await fetch(`${API_BASE}/api/extract-business-card`, {
        method: "POST",
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Card extract error:", data);
        alert(data?.error || "Error reading card — check backend logs.");
        return;
      }

      const parsed = data?.parsed && typeof data.parsed === "object" ? data.parsed : data;

      // be defensive about key names
      const nextFirstName =
        parsed.firstName ?? parsed.firstname ?? parsed.vorname ?? "";
      const nextLastName =
        parsed.lastName ?? parsed.lastname ?? parsed.nachname ?? "";
      const nextPhone = parsed.phone ?? parsed.telefon ?? parsed.phoneNumber ?? "";
      const nextEmail = parsed.email ?? parsed.eMail ?? parsed.mail ?? "";
      const nextCompany = parsed.company ?? parsed.unternehmen ?? "";
      const nextWebsite = parsed.website ?? parsed.web ?? parsed.url ?? "";

      setForm((prev) => ({
        ...prev,
        firstName: nextFirstName || prev.firstName || "",
        lastName: nextLastName || prev.lastName || "",
        phone: nextPhone || prev.phone || "",
        email: nextEmail || prev.email || "",
        company: nextCompany || prev.company || "",
        website: nextWebsite || prev.website || "",
      }));

      alert("Card scanned! Fields updated.");
    } catch (err) {
      console.error(err);
      alert("Error reading card — check backend logs.");
    } finally {
      setLoadingCard(false);
    }
  }

  // ---------- Dynamic steps ----------
  const needsInterest =
    form.category === "Servicepartner" || form.category === "Aftersales & Service";

  const interestOptions =
    form.category === "Servicepartner"
      ? INTEREST_SERVICEPARTNER
      : form.category === "Aftersales & Service"
      ? INTEREST_AFTERSALES
      : [];

  const needsZuliefererType = form.category === "Zulieferer";
  const needsSparePartFields = needsInterest && form.interest === "Ersatzteilanfrage";

  const steps = [
    { key: "contact", titleTop: "KONTAKT", title: "Kontakt" },
    { key: "category", titleTop: "KATEGORIE", title: "Kategorie" },
    ...(needsZuliefererType
      ? [{ key: "zulieferer", titleTop: "ZULIEFERER", title: "Zulieferer" }]
      : []),
    ...(needsInterest
      ? [{ key: "interest", titleTop: "INTERESSE", title: "Woran sind Sie interessiert?" }]
      : []),
    ...(needsSparePartFields
      ? [
          {
            key: "spareparts",
            titleTop: "ERSATZTEILANFRAGE",
            title: "Ersatzteilanfrage",
          },
        ]
      : []),
    { key: "products", titleTop: "PRODUKT", title: "Was wird gewünscht?" },
    { key: "tragkraft", titleTop: "TRAGKRAFT", title: "Tragkraft" },
    { key: "uploads", titleTop: "UPLOAD", title: "Anhänge" },
    { key: "notes", titleTop: "NOTIZEN", title: "Notizen" },
    { key: "review", titleTop: "ABSCHICKEN", title: "Zusammenfassung" },
    { key: "done", titleTop: "DONE", title: "Vielen Dank!" },
  ];

  const current = steps[stepIndex] || steps[0];
  const totalSteps = steps.length;

  function goNext() {
    setStepIndex((i) => Math.min(totalSteps - 1, i + 1));
  }

  function goBack() {
    setStepIndex((i) => Math.max(0, i - 1));
  }

  function nextDisabled() {
    switch (current.key) {
      case "contact":
        return !(
          form.firstName &&
          form.lastName &&
          form.company &&
          form.email &&
          form.phone
        );
      case "category":
        return !form.category;
      case "zulieferer":
        return !form.zuliefererType;
      case "interest":
        return !form.interest;
      case "spareparts":
        return !(form.boatType && form.serialNumber && form.sparePartType);
      case "products": {
        const hasAny = (form.products || []).length > 0 || !!form.productsOther;
        return !hasAny;
      }
      case "tragkraft":
        return !form.tragkraft;
      default:
        return false;
    }
  }

  // ---------- Upload handling ----------
  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const tooBig = incoming.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      alert(`${tooBig.name} is too large. Max ${MAX_FILE_MB}MB per file.`);
      return;
    }

    const merged = [...attachments, ...incoming];
    if (merged.length > MAX_FILES) {
      alert(`Max ${MAX_FILES} files allowed.`);
      return;
    }

    setAttachments(merged);
  }

  function removeFile(idx) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }

  // ---------- Submit ----------
  async function submitInquiry() {
    try {
      const fd = new FormData();

      const productsFinal = uniq([
        ...(form.products || []),
        form.productsOther ? form.productsOther.trim() : "",
      ]);

      const payload = {
        ...form,
        productsSemicolon: productsFinal.join("; "),
        submittedAt: new Date().toISOString(),
      };

      // ✅ backend expects "payload" json string
      fd.append("payload", JSON.stringify(payload));

      // ✅ send cardImage only if exists
      if (cardFile) fd.append("cardImage", cardFile);

      // ✅ attachments field matches backend
      attachments.forEach((f) => fd.append("attachments", f));

      const res = await fetch(`${API_BASE}/api/submit-inquiry`, {
        method: "POST",
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Submit error:", data);
        alert(data?.error || "Error submitting form. Check backend logs.");
        return;
      }

      setStepIndex(steps.findIndex((s) => s.key === "done"));
    } catch (err) {
      console.error(err);
      alert("Error submitting form. Check backend logs.");
    }
  }

  // ---------- UI helpers ----------
  function CheckboxItem({ checked, onChange, children }) {
    return (
      <label className="hb-checkItem">
        <input
          className="hb-checkInput"
          type="checkbox"
          checked={checked}
          onChange={onChange}
        />
        <span className="hb-checkBox" aria-hidden="true" />
        <span>{children}</span>
      </label>
    );
  }

  function RadioItem({ name, checked, onChange, children }) {
    return (
      <label className="hb-radioItem">
        <input type="radio" name={name} checked={checked} onChange={onChange} />
        <span>{children}</span>
      </label>
    );
  }

  function renderStepContent() {
    if (current.key === "contact") {
      return (
        <>
          <p className="hb-titleSmall">KONTAKT</p>
          <h1 className="hb-sectionTitle">Kontakt</h1>

          <div className="hb-formArea">
            <input
              placeholder="Venue / Veranstaltungsort (optional)"
              value={form.venue}
              onChange={(e) => setField("venue", e.target.value)}
            />
               <input
                placeholder="Submitter (optional)"
                value={form.submitter}
                onChange={(e) => setField("submitter", e.target.value)}
              />

            <div style={{ marginTop: 14, marginBottom: 14 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Business card photo (optional)
              </div>

              {mobile && (
                <div style={{ marginBottom: 10 }}>
                  <label className="hb-btn" style={{ display: "inline-block" }}>
                    📷 Use camera
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{ display: "none" }}
                      onChange={(e) => onCardFileChange(e.target.files?.[0])}
                    />
                  </label>
                </div>
              )}

              <input
                type="file"
                accept="image/*"
                onChange={(e) => onCardFileChange(e.target.files?.[0])}
              />

              {cardPreviewUrl && (
                <div style={{ marginTop: 12 }}>
                  <img
                    src={cardPreviewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.25)",
                    }}
                  />
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <button
                  className="hb-btn"
                  type="button"
                  onClick={autoFillFromCard}
                  disabled={!cardFile || loadingCard}
                >
                  {loadingCard ? "Reading..." : "Auto-fill from card"}
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <input
                placeholder="Vorname *"
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
              />
              <input
                placeholder="Nachname *"
                value={form.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
              />
              <input
                placeholder="Kundennummer (optional)"
                value={form.customerNumber}
                onChange={(e) => setField("customerNumber", e.target.value)}
              />
              <input
                placeholder="Unternehmen *"
                value={form.company}
                onChange={(e) => setField("company", e.target.value)}
              />
              <input
                placeholder="Telefonnummer *"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
              />
              <input
                placeholder="E-Mail *"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
              />
              <input
                placeholder="Website (optional)"
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
              />
            </div>
          </div>
        </>
      );
    }

    if (current.key === "category") {
      return (
        <>
          <p className="hb-titleSmall">KATEGORIE</p>
          <h1 className="hb-sectionTitle">Kategorie</h1>

          <div className="hb-formArea">
            <div className="hb-fieldLabel">
              Kategorie <span className="req">*</span>
            </div>

            <div className="hb-radioList">
              {CATEGORIES.map((v) => (
                <RadioItem
                  key={v}
                  name="category"
                  checked={form.category === v}
                  onChange={() => {
                    setForm((prev) => ({
                      ...prev,
                      category: v,
                      zuliefererType: "",
                      interest: "",
                      boatType: "",
                      serialNumber: "",
                      sparePartType: "",
                    }));
                  }}
                >
                  {v}
                </RadioItem>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (current.key === "zulieferer") {
      return (
        <>
          <p className="hb-titleSmall">ZULIEFERER</p>
          <h1 className="hb-sectionTitle">Zulieferer</h1>

          <div className="hb-formArea">
            <div className="hb-fieldLabel">
              Zulieferer <span className="req">*</span>
            </div>

            <div className="hb-radioList">
              {ZULIEFERER_TYPES.map((v) => (
                <RadioItem
                  key={v}
                  name="zuliefererType"
                  checked={form.zuliefererType === v}
                  onChange={() => setField("zuliefererType", v)}
                >
                  {v}
                </RadioItem>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (current.key === "interest") {
      return (
        <>
          <p className="hb-titleSmall">INTERESSE</p>
          <h1 className="hb-sectionTitle">Woran sind Sie interessiert?</h1>

          <div className="hb-formArea">
            <div className="hb-fieldLabel">
              Woran sind Sie interessiert? <span className="req">*</span>
            </div>

            <div className="hb-radioList">
              {interestOptions.map((v) => (
                <RadioItem
                  key={v}
                  name="interest"
                  checked={form.interest === v}
                  onChange={() => {
                    setForm((prev) => ({
                      ...prev,
                      interest: v,
                      boatType: "",
                      serialNumber: "",
                      sparePartType: "",
                    }));
                  }}
                >
                  {v}
                </RadioItem>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (current.key === "spareparts") {
      return (
        <>
          <p className="hb-titleSmall">ERSATZTEILANFRAGE</p>
          <h1 className="hb-sectionTitle">Ersatzteilanfrage</h1>

          <div className="hb-formArea">
            <div style={{ display: "grid", gap: 12 }}>
              <textarea
                placeholder="Bootstyp *"
                value={form.boatType}
                onChange={(e) => setField("boatType", e.target.value)}
                rows={3}
              />
              <input
                placeholder="Seriennummer *"
                value={form.serialNumber}
                onChange={(e) => setField("serialNumber", e.target.value)}
              />
              <textarea
                placeholder="Ersatzteilart *"
                value={form.sparePartType}
                onChange={(e) => setField("sparePartType", e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </>
      );
    }

    if (current.key === "products") {
      return (
        <>
          <p className="hb-titleSmall">PRODUKT</p>
          <h1 className="hb-sectionTitle">Was wird gewünscht?</h1>

          <div className="hb-formArea">
            <div className="hb-fieldLabel">
              Was wird gewünscht? <span className="req">*</span>
            </div>

            <div className="hb-checkList">
              {PRODUCT_OPTIONS.map((p) => (
                <CheckboxItem
                  key={p}
                  checked={form.products.includes(p)}
                  onChange={() => toggleInArray("products", p)}
                >
                  {p}
                </CheckboxItem>
              ))}
            </div>

            <div style={{ marginTop: 14 }}>
              <input
                placeholder="Sonstiges (optional)"
                value={form.productsOther}
                onChange={(e) => setField("productsOther", e.target.value)}
              />
            </div>

            <div style={{ marginTop: 10, opacity: 0.85 }}>
              Selected:{" "}
              {uniq([...(form.products || []), form.productsOther?.trim()]).join(
                "; "
              ) || "-"}
            </div>
          </div>
        </>
      );
    }

    if (current.key === "tragkraft") {
      return (
        <>
          <p className="hb-titleSmall">TRAGKRAFT</p>
          <h1 className="hb-sectionTitle">Tragkraft</h1>

          <div className="hb-formArea">
            <div className="hb-fieldLabel">
              Tragkraft <span className="req">*</span>
            </div>

            <div className="hb-radioList">
              {TRAGKRAFT_OPTIONS.map((v) => (
                <RadioItem
                  key={v}
                  name="tragkraft"
                  checked={form.tragkraft === v}
                  onChange={() => setField("tragkraft", v)}
                >
                  {v}
                </RadioItem>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (current.key === "uploads") {
      return (
        <>
          <p className="hb-titleSmall">UPLOAD</p>
          <h1 className="hb-sectionTitle">Anhänge</h1>

          <div className="hb-formArea">
            <div style={{ fontWeight: 600, marginBottom: 10 }}>
              Upload documents/photos (optional)
            </div>

            <div style={{ opacity: 0.9, marginBottom: 10 }}>
              Max {MAX_FILES} files • Max {MAX_FILE_MB}MB per file
            </div>

            <input
              type="file"
              multiple
              onChange={(e) => addFiles(e.target.files)}
            />

            {attachments.length > 0 && (
              <ul style={{ marginTop: 14, paddingLeft: 18, opacity: 0.95 }}>
                {attachments.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} style={{ marginBottom: 6 }}>
                    {f.name} ({(f.size / (1024 * 1024)).toFixed(2)} MB)
                    <button
                      type="button"
                      className="hb-btn"
                      style={{ marginLeft: 10, padding: "6px 10px" }}
                      onClick={() => removeFile(idx)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      );
    }

    if (current.key === "notes") {
      return (
        <>
          <p className="hb-titleSmall">NOTIZEN</p>
          <h1 className="hb-sectionTitle">Notizen</h1>

          <div className="hb-formArea">
            <textarea
              placeholder="Notizen..."
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              rows={7}
              style={{ width: "100%" }}
            />
          </div>
        </>
      );
    }

    if (current.key === "review") {
      const productsFinal = uniq([
        ...(form.products || []),
        form.productsOther ? form.productsOther.trim() : "",
      ]);

      return (
        <>
          <p className="hb-titleSmall">ABSCHICKEN</p>
          <h1 className="hb-sectionTitle">Zusammenfassung</h1>

          <div className="hb-formArea">
            <div style={{ lineHeight: 1.7, opacity: 0.95 }}>
              <div>
                <b>Venue:</b> {form.venue || "-"}
              </div>
              <div>
                <b>Submitter:</b> {form.submitter || "-"}
              </div>
              <div>
                <b>Kontakt:</b> {form.firstName} {form.lastName} — {form.company}
              </div>
              <div>
                <b>Telefon:</b> {form.phone || "-"}
              </div>
              <div>
                <b>E-Mail:</b> {form.email || "-"}
              </div>
              <div>
                <b>Branche:</b> {form.category || "-"}
              </div>

              {form.category === "Zulieferer" && (
                <div>
                  <b>Zulieferer:</b> {form.zuliefererType || "-"}
                </div>
              )}

              {(form.category === "Servicepartner" ||
                form.category === "Aftersales & Service") && (
                <div>
                  <b>Interesse:</b> {form.interest || "-"}
                </div>
              )}

              {needsSparePartFields && (
                <>
                  <div>
                    <b>Bootstyp:</b> {form.boatType || "-"}
                  </div>
                  <div>
                    <b>Seriennummer:</b> {form.serialNumber || "-"}
                  </div>
                  <div>
                    <b>Ersatzteilart:</b> {form.sparePartType || "-"}
                  </div>
                </>
              )}

              <div>
                <b>Produkte:</b>{" "}
                {productsFinal.length ? productsFinal.join("; ") : "-"}
              </div>
              <div>
                <b>Tragkraft:</b> {form.tragkraft || "-"}
              </div>
              <div>
                <b>Uploads:</b>{" "}
                {attachments.length ? `${attachments.length} file(s)` : "-"}
              </div>
              <div>
                <b>Notizen:</b> {form.notes || "-"}
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <button className="hb-btn" type="button" onClick={submitInquiry}>
                Submit inquiry
              </button>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <p className="hb-titleSmall">DONE</p>
        <h1 className="hb-sectionTitle">Vielen Dank!</h1>

        <div className="hb-formArea" style={{ textAlign: "center" }}>
          <div style={{ opacity: 0.9, lineHeight: 1.6 }}>
            Your inquiry has been submitted.
            <br />
            We will get back to you soon.
          </div>
        </div>
      </>
    );
  }

  const isDone = current.key === "done";
  const isReview = current.key === "review";

  return (
    <div className="hb-page">
      <div className="hb-stepTop">
        Step {stepIndex + 1} – {current.title} ({stepIndex + 1} / {totalSteps})
      </div>

      <div className="hb-card">
        <img src="/logo192.png" alt="HB" className="hb-logo" />

        {renderStepContent()}

        {!isDone && (
          <div className="hb-actions">
            {stepIndex > 0 && (
              <button className="hb-btn" type="button" onClick={goBack}>
                ← Zurück
              </button>
            )}

            {!isReview && (
              <button
                className="hb-btn"
                type="button"
                onClick={goNext}
                disabled={nextDisabled()}
              >
                Weiter →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}